import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnChanges,
  OnDestroy,
  ViewChild,
  effect,
  inject,
  untracked,
} from "@angular/core";
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";

import { SemaforoBsc } from "src/app/shared/model/bsc/bsc.model";
import { TemaService } from "src/app/shared/tema/tema.service";

/*
 * BSC · lo común a las gráficas del módulo (docs/BSC.md §7 «Gráficas»):
 * Chart.js 4 directo, como homologacion-modelos/radar-rubros. No se mezclan
 * apexcharts ni chartist.
 */
Chart.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  DoughnutController,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
);

/**
 * Los colores de las gráficas, como tokens. Un canvas no se pinta con CSS:
 * cada color se resuelve con getComputedStyle al dibujar (los --bsc-* los
 * pone bsc.shared.css en el host de la pantalla y se heredan; los --cos-*
 * solo existen en oscuro y cristal, así que cada uno lleva el respaldo del
 * clásico). Sin color-mix: getComputedStyle lo devuelve como color(srgb …)
 * y Chart.js no lo sabe aclarar para el hover.
 */
export const COLOR_BSC = {
  real: "var(--bsc-info, var(--cos-info, #1d4ed8))",
  /** T2: el año de referencia, en gris junto al que se compara. */
  comparado: "var(--bsc-neutro, #94a3b8)",
  programado: "var(--cos-texto-2, #475569)",
  objetivo: "var(--cos-acento, #b71c1c)",
  verde: "var(--bsc-exito, var(--cos-exito, #16a34a))",
  amarillo: "var(--bsc-aviso, var(--cos-aviso, #c2410c))",
  rojo: "var(--bsc-peligro, var(--cos-peligro, #b91c1c))",
  neutro: "var(--bsc-neutro, #64748b)",
  pista: "var(--cos-superficie-3, #e8edf2)",
  tinta: "var(--cos-texto, #253042)",
  suave: "var(--cos-texto-2, #64748b)",
  rejilla: "var(--cos-borde, rgba(148, 163, 184, 0.35))",
} as const;

/** El color de un semáforo (bsc-estados.ts SEMAFORO); sin semáforo o "inicia en Qn", el neutro. */
export function colorSemaforo(semaforo: SemaforoBsc | null | undefined): string {
  switch (semaforo) {
    case "VERDE":
      return COLOR_BSC.verde;
    case "AMARILLO":
      return COLOR_BSC.amarillo;
    case "ROJO":
      return COLOR_BSC.rojo;
    default:
      return COLOR_BSC.neutro;
  }
}

/**
 * Base de las tres gráficas: dibuja al montar, al cambiar las entradas y al
 * cambiar de tema (effect sobre TemaService.actual, que pone data-tema en
 * <html> antes de que corra el efecto), y destruye la gráfica al salir. La
 * plantilla de cada gráfica trae `<canvas #lienzo>` siempre presente (no
 * dentro de un *ngIf), con su caja de alto fijo: `maintainAspectRatio:false`.
 */
@Directive()
export abstract class GraficaBsc implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild("lienzo") protected lienzo?: ElementRef<HTMLCanvasElement>;
  private grafica: Chart | null = null;
  private readonly tema = inject(TemaService);

  constructor() {
    effect(() => {
      this.tema.actual();
      untracked(() => this.dibuja());
    });
  }

  ngAfterViewInit(): void {
    this.dibuja();
  }

  ngOnChanges(): void {
    if (this.lienzo) {
      this.dibuja();
    }
  }

  ngOnDestroy(): void {
    this.grafica?.destroy();
    this.grafica = null;
  }

  /** La gráfica de esta pantalla sobre el lienzo, o null si no hay nada que pintar. */
  protected abstract arma(lienzo: HTMLCanvasElement): Chart | null;

  /** El color que da el tema vigente a un token (o a un literal). */
  protected color(token: string): string {
    const caja = this.lienzo?.nativeElement.parentElement;
    if (!caja) {
      return token;
    }
    const sonda = document.createElement("span");
    sonda.style.display = "none";
    sonda.style.color = token;
    caja.appendChild(sonda);
    const valor = getComputedStyle(sonda).color;
    sonda.remove();
    return valor || token;
  }

  private dibuja(): void {
    this.grafica?.destroy();
    this.grafica = null;
    if (this.lienzo) {
      this.grafica = this.arma(this.lienzo.nativeElement);
    }
  }
}
